/**
 * useMembershipCertificate — consumer hook for MembershipCertificatePort.
 *
 * Reads the membershipCertificate port from PortsContext, resolves the
 * certificate via React.use() (Suspense), and exposes obtainDocument as a
 * passthrough callback. Throws a clear error when the port slot is absent.
 *
 * Promise-caching discipline is identical to `useMemberProfile` (034 US2) /
 * `useCases` (034 US1) / `useMyData` (033), and for the same reason: since 049
 * the production port is genuinely ASYNC (`RealMembershipCertificateAdapter`
 * reads the member profile + billing over tRPC), and a component that suspends
 * on its INITIAL mount has its fiber discarded — so a `useRef`-cached promise is
 * recreated on every retry and the boundary loops forever. The cache therefore
 * lives OUTSIDE React in a module-level WeakMap keyed by the port instance: it
 * survives the suspend-retry, resets when the composition swaps the adapter (new
 * key), and is GC'd with the port.
 *
 * (Before 049 this hook cached in a `useRef` inside a `useMemo` and called
 * `React.use()` from within that memo — both a rules-of-hooks violation and the
 * loop described above. It went unnoticed because the only adapter then was the
 * SYNCHRONOUS demo one, which never reached the promise branch at all.)
 *
 * obtainDocument() is intentionally NOT cached: each call mints a fresh object
 * URL and the consumer revokes it after triggering the download (see the port
 * contract). Passing it straight through keeps that lifecycle explicit. It is
 * also a MUTATION (it audits a generation server-side), so it must never be
 * folded into the read cache.
 *
 * Only call inside components wrapped in a Suspense boundary; Profile.tsx wraps
 * the certificate view in PortErrorBoundary -> Suspense.
 *
 * Distinct from the providers/useMembershipCertificatePort.ts FACTORY hook: that
 * one builds the adapter for the composition root, this one consumes it.
 *
 * Owned by feature 026-proto-convergence-v2; reshaped by
 * 049-membership-certificate.
 * Contract: specs/049-membership-certificate/contracts/certificate-generation.md
 */

import React, { useContext } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type {
  CertificateDocument,
  MembershipCertificate,
  MembershipCertificatePort,
} from './MembershipCertificatePort.js';

export interface UseMembershipCertificateResult {
  readonly certificate: MembershipCertificate;
  readonly obtainDocument: () => CertificateDocument | PromiseLike<CertificateDocument>;
}

interface CertificateCacheEntry {
  readonly promise: PromiseLike<MembershipCertificate>;
  rejected: boolean;
}

const certificatePromiseCache = new WeakMap<MembershipCertificatePort, CertificateCacheEntry>();

/**
 * Pending unmount evictions. A RESOLVED preview is kept only while a consumer
 * is mounted: leaving the view evicts it, so re-entering re-reads the server
 * preview and the on-screen fields cannot drift from a freshly generated PDF
 * for the rest of the tab (O-021). The eviction is DEFERRED a macrotask and
 * cancelled by a re-running effect, so StrictMode's mount → cleanup → mount
 * does not evict (and re-suspend) a still-mounted section.
 */
const pendingEvictions = new WeakMap<MembershipCertificatePort, ReturnType<typeof setTimeout>>();

/**
 * Mounted consumers per port. Eviction is scheduled only when the LAST mounted
 * consumer of a port unmounts, so a second concurrent consumer can never have
 * the preview evicted (and re-suspend) underneath it.
 */
const mountedConsumers = new WeakMap<MembershipCertificatePort, number>();

function isPromiseLike<T>(value: T | PromiseLike<T>): value is PromiseLike<T> {
  return value !== null && typeof value === 'object' && 'then' in value;
}

export function useMembershipCertificate(): UseMembershipCertificateResult {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useMembershipCertificate: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.membershipCertificate === undefined) {
    throw new Error(
      'useMembershipCertificate: membershipCertificate port is not registered. This feature requires the membership certificate port to be configured.',
    );
  }

  const port: MembershipCertificatePort = ports.membershipCertificate;

  React.useEffect(() => {
    mountedConsumers.set(port, (mountedConsumers.get(port) ?? 0) + 1);
    const pending = pendingEvictions.get(port);
    if (pending !== undefined) {
      clearTimeout(pending);
      pendingEvictions.delete(port);
    }
    return () => {
      const remaining = (mountedConsumers.get(port) ?? 1) - 1;
      mountedConsumers.set(port, remaining);
      if (remaining > 0) return;
      pendingEvictions.set(
        port,
        setTimeout(() => {
          pendingEvictions.delete(port);
          if ((mountedConsumers.get(port) ?? 0) === 0) {
            certificatePromiseCache.delete(port);
          }
        }, 0),
      );
    };
  }, [port]);
  const obtainDocument = (): CertificateDocument | PromiseLike<CertificateDocument> =>
    port.obtainDocument();

  let entry = certificatePromiseCache.get(port);
  if (entry === undefined) {
    const certificateOrPromise = port.getCertificate();
    if (!isPromiseLike(certificateOrPromise)) {
      // Synchronous adapter (test fixture) — nothing to suspend on.
      return { certificate: certificateOrPromise, obtainDocument };
    }
    const newEntry: CertificateCacheEntry = { promise: certificateOrPromise, rejected: false };
    certificateOrPromise.then(undefined, () => {
      newEntry.rejected = true;
    });
    certificatePromiseCache.set(port, newEntry);
    entry = newEntry;
  } else if (entry.rejected) {
    // Deferred eviction (the useMemberProfile / useCases rationale): a
    // permanently-cached rejected promise would brick the surface on every later
    // mount — and this read DOES reject in normal operation (FR-008: a member
    // whose membership start date is missing). Eager eviction instead makes
    // React's synchronous error-retry re-fetch in an unbounded loop.
    // queueMicrotask lands the eviction AFTER React commits the boundary
    // fallback — retries still observe the rejected entry; a later mount
    // (navigate away and back) finds an empty cache and re-reads.
    queueMicrotask(() => certificatePromiseCache.delete(port));
  }

  return { certificate: React.use(entry.promise), obtainDocument };
}
